import React, { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { isPageCovered, subscribeToPageCover } from './LoadingScreen';
import { buildCoverInertScript, setContentCovered } from './coverInert';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const getServerSnapshot = () => true;
// Leaves the layout to the content, as if this wrapper weren't there.
const WRAPPER_STYLE = { display: 'contents' };
const coverInertScript = buildCoverInertScript();

// Wraps the page content behind the first-load cover (LoadingScreen) and makes it inert
// until the cover is revealed, so keyboard and screen-reader users can't reach controls
// they can't see or click. Only the app's own content is wrapped, never <body>, so dialogs
// injected outside it (the Cookiebot consent prompt, which sits above the cover) stay usable.
// Releasing it never moves focus: nothing inside could hold focus while covered.
// `inert` is managed on the DOM node rather than as a prop, so the attribute an inline
// script applies to server-rendered content (`serverRendered`) doesn't trip hydration.
const CoveredContent = ({ children, serverRendered = false }) => {
  const ref = useRef(null);
  const covered = useSyncExternalStore(subscribeToPageCover, isPageCovered, getServerSnapshot);

  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    const isCovered = isPageCovered();
    setContentCovered(element, isCovered);
    if (!isCovered) return undefined;

    const keepFocusOut = (event) => {
      if (element.contains(event.target)) event.target.blur?.();
    };
    document.addEventListener('focusin', keepFocusOut);
    return () => document.removeEventListener('focusin', keepFocusOut);
  }, [covered]);

  return (
    <div ref={ref} style={WRAPPER_STYLE} data-first-load-covered="" suppressHydrationWarning>
      {serverRendered && <script dangerouslySetInnerHTML={{ __html: coverInertScript }} />}
      {children}
    </div>
  );
};

export default CoveredContent;
